"use client";

import { Menu, User, Settings, Users, LogOut, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NotificationsDropdown } from "./NotificationsDropdown";
import { SearchCommand } from "./SearchCommand";
import { useTourContext } from "@/contexts/TourContext";
import { useTranslations } from "@/hooks/useTranslations";
import * as React from "react";

interface HeaderProps {
  userName?: string;
  userEmail?: string;
  userImage?: string | null;
  userRole?: string;
  hasCompletedOnboarding?: boolean;
  createdAt?: Date | string;
  onMenuClick?: () => void;
}

export function Header({
  userName = "User",
  userEmail = "user@example.com",
  userImage,
  userRole = "STAFF",
  hasCompletedOnboarding = false,
  createdAt,
  onMenuClick,
}: HeaderProps) {
  const { resetTour } = useTourContext();
  const t = useTranslations('header');
  const tNav = useTranslations('navigation');

  const initials = userName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const isOwner = userRole === "OWNER";

  // Determine if user is a first-time user
  // A user is considered "first-time" if either:
  // 1. They haven't completed onboarding, OR
  // 2. Their account was created within the last 24 hours
  const isFirstTimeUser = React.useMemo(() => {
    if (!hasCompletedOnboarding) {
      return true;
    }

    if (createdAt) {
      const accountCreatedDate = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
      const hoursSinceCreation = (Date.now() - accountCreatedDate.getTime()) / (1000 * 60 * 60);
      return hoursSinceCreation < 24;
    }

    return false;
  }, [hasCompletedOnboarding, createdAt]);

  const greeting = isFirstTimeUser ? t('welcome') : t('welcomeBack');

  return (
    <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 sm:px-6 dark:border-gray-800 dark:bg-gray-950 sticky top-0 z-40">
      <div className="flex items-center gap-3 sm:gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden min-h-[44px] min-w-[44px]"
          onClick={onMenuClick}
          aria-label={tNav('openMenu')}
        >
          <Menu className="h-6 w-6" />
        </Button>
        <h1 className="text-base sm:text-xl font-semibold truncate">
          {greeting}, {userName.split(" ")[0]}
        </h1>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        {/* Inline Search */}
        <div className="hidden sm:block min-w-[200px] sm:min-w-[320px]">
          <SearchCommand />
        </div>

        {/* Notifications */}
        <NotificationsDropdown />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild data-tour="user-menu">
            <Button
              variant="ghost"
              className="relative h-10 w-10 min-h-[44px] min-w-[44px] rounded-full"
              aria-label={t('userMenuFor', { name: userName })}
            >
              <Avatar>
                <AvatarImage src={userImage || undefined} alt={userName} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{userName}</p>
                <p className="text-xs leading-none text-gray-500 dark:text-gray-400">
                  {userEmail}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href="/settings/profile" className="flex items-center gap-2 cursor-pointer">
                <User className="h-4 w-4" />
                {tNav('profile')}
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href="/settings" className="flex items-center gap-2 cursor-pointer">
                <Settings className="h-4 w-4" />
                {tNav('settings')}
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={resetTour} className="flex items-center gap-2 cursor-pointer">
              <GraduationCap className="h-4 w-4" />
              {t('takeTour')}
            </DropdownMenuItem>
            {isOwner && (
              <DropdownMenuItem asChild>
                <a href="/team" className="flex items-center gap-2 cursor-pointer">
                  <Users className="h-4 w-4" />
                  {tNav('team')}
                </a>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href="/api/auth/signout" className="flex items-center gap-2 cursor-pointer text-error">
                <LogOut className="h-4 w-4" />
                {tNav('logOut')}
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
